"use client";

import { ErrorCard } from "@/components/ui/ErrorCard";
import { LoadingCards } from "@/components/ui/LoadingCards";
import { API_ENDPOINTS, FORM_CONFIG, FORM_MESSAGES, PAGE_CONTENT } from "@/constants/app.constants";
import { ContactFormData, ContactFormErrors } from "@/types/contact-form.type";
import { MeData } from "@/types/me.type";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import * as yup from "yup";

const validationSchema = yup.object({
  name: yup.string().required(FORM_MESSAGES.NAME_REQUIRED).min(2, FORM_MESSAGES.NAME_MIN),
  email: yup.string().required(FORM_MESSAGES.EMAIL_REQUIRED).email(FORM_MESSAGES.EMAIL_INVALID),
  subject: yup.string().required(FORM_MESSAGES.SUBJECT_REQUIRED).min(5, FORM_MESSAGES.SUBJECT_MIN),
  message: yup.string().required(FORM_MESSAGES.MESSAGE_REQUIRED).min(10, FORM_MESSAGES.MESSAGE_MIN),
});

const fetchMe = async (): Promise<MeData> => {
  const response = await fetch(API_ENDPOINTS.ME);
  if (!response.ok) {
    throw new Error("Failed to load profile");
  }
  return response.json();
};

export default function ContactPage() {
  const {
    data: meData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["me"],
    queryFn: fetchMe,
  });

  const [formData, setFormData] = useState<ContactFormData>({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const [errors, setErrors] = useState<ContactFormErrors>({});
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for this field when user starts typing
    if (errors[name as keyof ContactFormData]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});
    setSubmitError("");

    try {
      // Validate form data
      await validationSchema.validate(formData, { abortEarly: false });

      // Prepare email HTML content
      const emailHtml = `
        <h2>New Contact Form Submission</h2>
        <p><strong>Name:</strong> ${formData.name}</p>
        <p><strong>Email:</strong> ${formData.email}</p>
        <p><strong>Subject:</strong> ${formData.subject}</p>
        <hr />
        <h3>Message:</h3>
        <p>${formData.message.replace(/\n/g, "<br />")}</p>
      `;

      // Send email via API
      const response = await fetch(API_ENDPOINTS.SEND_EMAIL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          from: formData.email,
          subject: `Contact Form: ${formData.subject}`,
          html: emailHtml,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Failed to send email");
      }

      // Success
      setSubmitted(true);
      setFormData({ name: "", email: "", subject: "", message: "" });
      setTimeout(() => setSubmitted(false), FORM_CONFIG.SUCCESS_TIMEOUT);
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        const newErrors: ContactFormErrors = {};
        error.inner.forEach((err) => {
          if (err.path) {
            newErrors[err.path as keyof ContactFormData] = err.message;
          }
        });
        setErrors(newErrors);
      } else if (error instanceof Error) {
        setSubmitError(error.message);
      } else {
        setSubmitError("An unexpected error occurred. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const getFieldClassName = (fieldName: keyof ContactFormData) => {
    const baseClass =
      "w-full px-3 py-2 mc-slot bg-input text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200";
    return errors[fieldName] ? `${baseClass} ring-2 ring-destructive` : baseClass;
  };

  return (
    <>
      <section className="px-5 py-10 max-w-6xl mx-auto">
        <div className="mb-8 animate-fadeIn">
          <h1 className="text-3xl text-primary mb-3 uppercase">{PAGE_CONTENT.CONTACT.TITLE}</h1>
          <p className="text-muted-foreground text-lg">{PAGE_CONTENT.CONTACT.DESCRIPTION}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Contact Information */}
          <div className="animate-slideInLeft">
            <h2 className="text-xl text-primary mb-6 uppercase">{PAGE_CONTENT.CONTACT.INFO_TITLE}</h2>

            {isLoading ? (
              <LoadingCards count={5} />
            ) : isError ? (
              <ErrorCard title="Failed to load profile." message={FORM_MESSAGES.PROFILE_LOAD_ERROR} />
            ) : meData ? (
              <div className="space-y-5">
                <div className="mc-panel p-4 hover:translate-y-[-2px] transition-transform duration-200 animate-scaleIn">
                  <h3 className="text-primary mb-2 uppercase text-base">Name</h3>
                  <p className="transition-colors duration-200">
                    {meData.title} {meData.firstName} {meData.lastName}
                  </p>
                </div>

                <div className="mc-panel p-4 hover:translate-y-[-2px] transition-transform duration-200 animate-scaleIn">
                  <h3 className="text-primary mb-2 uppercase text-base">Phone</h3>
                  <a
                    href={`tel:${meData.phone}`}
                    className="text-secondary hover:text-primary transition-all duration-200"
                  >
                    {meData.phone}
                  </a>
                </div>

                <div className="mc-panel p-4 hover:translate-y-[-2px] transition-transform duration-200 animate-scaleIn">
                  <h3 className="text-primary mb-2 uppercase text-base">Email</h3>
                  <a
                    href={`mailto:${meData.email}`}
                    className="text-secondary hover:text-primary transition-all duration-200"
                  >
                    {meData.email}
                  </a>
                </div>

                <div className="mc-panel p-4 hover:translate-y-[-2px] transition-transform duration-200 animate-scaleIn">
                  <h3 className="text-primary mb-2 uppercase text-base">LinkedIn</h3>
                  <a
                    href={meData.linkedIn.startsWith("http") ? meData.linkedIn : `https://${meData.linkedIn}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-secondary hover:text-primary transition-all duration-200"
                  >
                    {meData.linkedIn}
                  </a>
                </div>

                <div className="mc-panel p-4 hover:translate-y-[-2px] transition-transform duration-200 animate-scaleIn">
                  <h3 className="text-primary mb-2 uppercase text-base">Address</h3>
                  <p className="text-sm transition-colors duration-200">
                    {meData.address.addressNo} {meData.address.building}
                    <br />
                    {meData.address.subDistrict}, {meData.address.district}
                    <br />
                    {meData.address.city} {meData.address.postalCode}
                  </p>
                </div>
              </div>
            ) : null}
          </div>

          {/* Contact Form */}
          <div className="animate-slideInRight">
            <h2 className="text-xl text-primary mb-6 uppercase">{PAGE_CONTENT.CONTACT.FORM_TITLE}</h2>

            <form onSubmit={handleSubmit} className="mc-panel p-6">
              {submitted && (
                <div className="mb-4 p-4 mc-slot bg-primary/20 text-primary animate-slideDown">
                  <p className="uppercase">✓ Achievement Unlocked!</p>
                  <p>{FORM_MESSAGES.SUBMIT_SUCCESS}</p>
                </div>
              )}

              {submitError && <ErrorCard title="✗ Error" message={submitError} />}

              <div className="mb-5">
                <label
                  htmlFor="name"
                  className="block text-base uppercase text-primary mb-2 transition-colors duration-200"
                >
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className={getFieldClassName("name")}
                  placeholder="Your name"
                />
                {errors.name && <p className="mt-2 text-sm text-destructive animate-pulse">{errors.name}</p>}
              </div>

              <div className="mb-5">
                <label
                  htmlFor="email"
                  className="block text-base uppercase text-primary mb-2 transition-colors duration-200"
                >
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className={getFieldClassName("email")}
                  placeholder="your@email.com"
                />
                {errors.email && <p className="mt-2 text-sm text-destructive animate-pulse">{errors.email}</p>}
              </div>

              <div className="mb-5">
                <label
                  htmlFor="subject"
                  className="block text-base uppercase text-primary mb-2 transition-colors duration-200"
                >
                  Subject *
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className={getFieldClassName("subject")}
                  placeholder="How can I help?"
                />
                {errors.subject && <p className="mt-2 text-sm text-destructive animate-pulse">{errors.subject}</p>}
              </div>

              <div className="mb-6">
                <label
                  htmlFor="message"
                  className="block text-base uppercase text-primary mb-2 transition-colors duration-200"
                >
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  rows={5}
                  className={`${getFieldClassName("message")} resize-none`}
                  placeholder="Your message..."
                />
                {errors.message && <p className="mt-2 text-sm text-destructive animate-pulse">{errors.message}</p>}
              </div>

              <button type="submit" disabled={isSubmitting} className="mc-btn w-full px-4 py-3 uppercase tracking-wide">
                {isSubmitting ? (
                  <span className="inline-flex items-center gap-2">
                    <span className="inline-block w-4 h-4 border-2 border-transparent border-t-primary-foreground rounded-full animate-spin"></span>
                    {FORM_MESSAGES.SUBMIT_LOADING}
                  </span>
                ) : (
                  FORM_MESSAGES.SUBMIT_BUTTON
                )}
              </button>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}
